import { useEffect, useRef, useState as useStateRaw, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import HeroSection from './components/HeroSection';
import PainPoints from './components/PainPoints';
import WhyButlerWorks from './components/WhyButlerWorks';
import RecommendedPrograms from './components/RecommendedPrograms';
import ScenarioTraining from './components/ScenarioTraining';
import TargetRoles from './components/TargetRoles';
import PricingTable from './components/PricingTable';
import FlowSteps from './components/FlowSteps';
import ContactForm from './components/ContactForm';
import FAQSection from './components/FAQSection';
import CTASection from './components/CTASection';
import RelatedPages from './components/RelatedPages';
import Seo from '@/components/seo/Seo';
import { useLanguageContext } from '@/i18n/LanguageContext';

function FadeInSection({ children, className = '' }: { children: ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useStateRaw(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className={`fade-in-section ${isVisible ? 'is-visible' : ''} ${className}`}>
      {children}
    </div>
  );
}

function splitParagraphs(value: string): string[] {
  const parts = value.split(/\n{2,}/).filter((part) => part.length > 0);
  return parts.length > 0 ? parts : [value];
}

export default function CorporateClinicPage() {
  const { lang } = useLanguageContext();
  const { t } = useTranslation();

  const serviceStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: lang === 'en'
      ? 'Corporate Training for Medical & Aesthetic Clinics | Elevating Private-Pay Service Standards'
      : '医療・美容クリニック向け接遇研修｜自由診療の品格を整える',
    provider: {
      '@type': 'Organization',
      name: lang === 'en' ? 'The All Japan Butler Association' : '一般社団法人全日本執事協会',
    },
    serviceType: lang === 'en' ? 'Corporate Training for Medical Institutions' : '医療機関向け接遇研修',
    areaServed: {
      '@type': 'Country',
      name: lang === 'en' ? 'Japan' : '日本',
    },
    description: lang === 'en'
      ? 'Corporate training for medical and aesthetic clinics. From 220,000 JPY (tax incl.) for a half-day session. On-site delivery available, including after-hours and closed-day scheduling. Instilling butler-standard hospitality in private-pay clinical settings.'
      : '医療・美容クリニック向け接遇研修。半日220,000円税込〜、出張対応。夜間・休診日開催可能。自由診療にふさわしい接遇と気遣いを執事教育の手法で院内に定着させます。',
  };

  const breadcrumbStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: lang === 'en' ? 'Home' : 'ホーム', item: 'https://butler-academy.jp/' },
      { '@type': 'ListItem', position: 2, name: lang === 'en' ? 'Corporate Training' : '法人研修', item: 'https://butler-academy.jp/corporate' },
      { '@type': 'ListItem', position: 3, name: lang === 'en' ? 'Medical & Aesthetic Clinics' : '医療・美容クリニック向け', item: 'https://butler-academy.jp/corporate/clinic' },
    ],
  };

  return (
    <>
      <Header />
      <main>
      <Seo route="/corporate/clinic" />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceStructuredData) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbStructuredData) }}
      />

      <HeroSection />

      <FadeInSection>
        <PainPoints />
      </FadeInSection>

      <FadeInSection>
        <WhyButlerWorks />
      </FadeInSection>

      <FadeInSection>
        <RecommendedPrograms />
      </FadeInSection>

      <FadeInSection>
        <ScenarioTraining />
      </FadeInSection>

      <FadeInSection>
        <TargetRoles />
      </FadeInSection>

      <FadeInSection>
        <PricingTable />
      </FadeInSection>

      <FadeInSection>
        <FlowSteps />
      </FadeInSection>

      <FadeInSection>
        <ContactForm />
      </FadeInSection>

      <FadeInSection>
        <FAQSection />
      </FadeInSection>

      <FadeInSection>
        <CTASection />
      </FadeInSection>

      <FadeInSection>
        <RelatedPages />
      </FadeInSection>

      <div className="bg-background-50 py-8">
        {splitParagraphs(t('corporateClinic.page.lastUpdated')).map((paragraph, index) => (
          <p key={index} className="text-center text-[13px] text-foreground-400 tracking-[0.05em]">
            {paragraph}
          </p>
        ))}
      </div>

      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="fixed bottom-8 right-8 w-11 h-11 rounded-full border border-accent-500/40 text-accent-500 hover:bg-accent-500/10 hover:border-accent-400 transition-all duration-300 flex items-center justify-center cursor-pointer z-40"
        aria-label={t('common.backToTop')}
      >
        <i className="ri-arrow-up-line"></i>
      </button>
    </main>
      <Footer />
    </>
  );
}
